import React, { useState, useEffect } from 'react';
import './CreateEditNotification.scss';
import { redirect, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Cookies from "js-cookie";
import {
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    MenuItem,
    Button,
} from '@mui/material';
import { getStateAction } from '../../actions/permissionAction';
import {
    createNotificationScheduleAction,
    getNotificationDetailsAction,
    getUserGroupAction,
    getRedirectionPageAction,
    updateNotificationAction
} from '../../actions/NotificationAction';
import { HTTP_STATUS } from '../../constants/httpstatus/HttpsStatus';
import { NotificationManager } from 'react-notifications';

const CreateEditNotification = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { i18n, t } = useTranslation();
    const language = Cookies.get("language");
    const [selectedStates, setSelectedStates] = useState([]);
    const { states } = useSelector((state) => state.getStatesReducer);
    const { singleNotification } = useSelector((state) => state.notificationDetailsReducer);
    const { userGroupList } = useSelector((state) => state.getUserGroupReducer);
    const { redirectionPageList } = useSelector((state) => state.getRedirectionPageReducer);
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [isAllStates, setIsAllStates] = useState(false);
    const [isAllUsers, setIsAllUsers] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const detailsData = singleNotification?.results?.[0] || {};

    const [formData, setFormData] = useState({
        title: '',
        body: '',
        starting_date: '',
        starting_time: '',
        state_ids: [],
        redirection_page: '',
        user_group_ids: []
    });

    const handleChangeLanguage = (language) => {
        i18n.changeLanguage(language);
    };

    useEffect(() => {
        handleChangeLanguage(language);
        dispatch(getStateAction());
        dispatch(getUserGroupAction());
        dispatch(getRedirectionPageAction());

        if (isEditMode) {
            dispatch(getNotificationDetailsAction(id));
        }
    }, [dispatch, language, isEditMode]);

    const handleOnClose = () => {
        navigate('/notification_list')
    }

    useEffect(() => {
        if (isEditMode && detailsData && Object.keys(detailsData).length > 0) {
            const stateIds = detailsData.states?.map((s) => s.id) || [];
            const userGroupIds = detailsData.user_groups?.map((g) => g.id) || [];
            const redirectionPageId = detailsData.redirection_page?.id || '';

            setFormData({
                title: detailsData.title || '',
                body: detailsData.body || '',
                starting_date: detailsData.starting_date || '',
                starting_time: detailsData.starting_time || '',
                state_ids: stateIds,
                redirection_page: redirectionPageId,
                user_group_ids: userGroupIds
            });

            setSelectedStates(stateIds);
        }
    }, [isEditMode, detailsData]);

    const handleStateChange = (event) => {
        const selectedOptions = event.target.value;

        if (selectedOptions.includes("all")) {
            if (selectedStates.length === states.length) {
                // Unselect all
                setSelectedStates([]);
                setIsAllStates(false);
                setFormData({ ...formData, state_ids: [] });
            } else {
                // Select all
                const allStateIds = states.map(state => state.id);
                setSelectedStates(allStateIds);
                setIsAllStates(true);
                setFormData({ ...formData, state_ids: allStateIds });
            }
        } else {
            setSelectedStates(selectedOptions);
            setIsAllStates(false);
            setFormData({ ...formData, state_ids: selectedOptions });
        }
    };

    const handleUserGroupChange = (event) => {
        const selectedOptions = event.target.value;

        if (selectedOptions.includes("all")) {
            if (formData.user_group_ids.length === userGroupList.length) {
                setFormData({ ...formData, user_group_ids: [] });
                setIsAllUsers(false);
            } else {
                const allUserGroupIds = userGroupList.map(group => group.id);
                setFormData({ ...formData, user_group_ids: allUserGroupIds });
                setIsAllUsers(true);
            }
        } else {
            setFormData({ ...formData, user_group_ids: selectedOptions });
            setIsAllUsers(false);
        }
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const selectedDateTime = new Date(`${formData.starting_date}T${formData.starting_time}`);
        const now = new Date();
        if (selectedDateTime < now) {
            NotificationManager.error(t("notification.timeValidation"));
            setIsLoading(false);
            return;
        }
        const payload = {
            title: formData.title,
            body: formData.body,
            starting_date: formData.starting_date,
            starting_time: formData.starting_time,
            redirection_page: formData.redirection_page,
            is_all_states: isAllStates,
            is_all_users: isAllUsers,
        };

        if (!isAllStates) {
            payload.state_ids = selectedStates;
        }

        if (!isAllUsers) {
            payload.user_group_ids = formData.user_group_ids;
        }

        let response;
        if (isEditMode) {
            response = await dispatch(updateNotificationAction(id, payload));
        } else {
            response = await dispatch(createNotificationScheduleAction(payload));
        }

        const { status, message } = response;
        if (status === HTTP_STATUS.CREATED || status === HTTP_STATUS.SUCCESS) {
            NotificationManager.success(message);
            navigate('/notification_list');
        } else {
            NotificationManager.error(message);
        }
        setIsLoading(false);
    };



    const isFormValid = () => {
        return (
            formData.title?.trim() &&
            formData.body?.trim() &&
            formData.starting_date &&
            formData.starting_time &&
            formData.state_ids.length > 0 &&
            formData.user_group_ids.length > 0 &&
            formData.redirection_page
        );
    };

    return (
        <div className='create-notification-container'>
            <div className='create-notification-content'>
                <div className='create-notification-section'>
                    <div className="header-navigation">
                        <p className="create-notification-text">TVS &gt; {isEditMode ? t('notification.editNotification') : t('notification.createNotification')}</p>
                    </div>
                    <div className='create-notification-main'>
                        <h3 className='create-notification-heading'>
                            {isEditMode ? t('notification.editNotification') : t('notification.createNotification')}
                        </h3>
                        <div className='create-notification-form'>
                            <form className='notification-form'>
                                <div className='input-flex'>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('common.provinceState')}</label>
                                        <FormControl className="multiselect_part">
                                            <Select
                                                className="multipleSelect_part"
                                                labelId="state-label"
                                                id="state-select"
                                                name="state"
                                                multiple
                                                displayEmpty
                                                inputProps={{ className: 'multiselect_input' }}
                                                value={selectedStates}
                                                onChange={handleStateChange}
                                                renderValue={(selected) =>
                                                    selected?.length === 0
                                                        ? <span>{t('RolesAndPermission.selectState')}</span>
                                                        : selected
                                                            .map((stateId) => states?.find((s) => s?.id === stateId)?.name)
                                                            .filter(Boolean)
                                                            .join(', ')
                                                }
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: '200px',
                                                        },
                                                    },
                                                }}
                                            >
                                                {states?.length > 0 && (
                                                    <MenuItem className="partlist_menu" key="all" value="all">
                                                        <Checkbox checked={selectedStates?.length === states?.length && states?.length > 0} />
                                                        <ListItemText primary={t('common.selectAll')} />
                                                    </MenuItem>
                                                )}
                                                {states?.map((state) => (
                                                    <MenuItem className="partlist_menu" key={state?.id} value={state?.id}>
                                                        <Checkbox checked={selectedStates.includes(state?.id)} />
                                                        <ListItemText primary={state?.name} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('notification.heading')}</label>
                                        <input
                                            type="text"
                                            placeholder={t('notification.enterHeading')}
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('common.selectGroup')}</label>
                                        <FormControl className="multiselect_part">
                                            <Select
                                                className="multipleSelect_part"
                                                labelId="user-group-label"
                                                id="user-group-select"
                                                name="user_group_ids"
                                                multiple
                                                displayEmpty
                                                inputProps={{ className: 'multiselect_input' }}
                                                value={formData.user_group_ids}
                                                onChange={handleUserGroupChange}
                                                renderValue={(selected) =>
                                                    selected?.length === 0
                                                        ? <span>{t('common.selectGroup')}</span>
                                                        : selected
                                                            .map((id) => userGroupList.find((group) => group.id === id)?.display_name)
                                                            .filter(Boolean)
                                                            .join(', ')
                                                }
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: '200px',
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem key="all" value="all">
                                                    <Checkbox checked={formData.user_group_ids.length === userGroupList.length} />
                                                    <ListItemText primary={t('common.selectAll')} />
                                                </MenuItem>
                                                {userGroupList.map((group) => (
                                                    <MenuItem key={group.id} value={group.id}>
                                                        <Checkbox checked={formData.user_group_ids.includes(group.id)} />
                                                        <ListItemText primary={group.display_name} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                    </div>
                                </div>

                                <div className="form-group-description">
                                    <label className='notification-label-description required'>{t('common.description')}</label>
                                    <textarea
                                        placeholder={t('common.enterDescription')}
                                        value={formData.body}
                                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                    ></textarea>
                                </div>
                                <div className='input-flex'>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('notification.redirectionPage')}</label>
                                        <select
                                            value={formData.redirection_page}
                                            onChange={(e) => setFormData({ ...formData, redirection_page: Number(e.target.value) })}
                                        >
                                            <option value="">{t('notification.selectRedirectionPage')}</option>

                                            {redirectionPageList?.map((page) => (
                                                <option key={page?.id} value={page?.id}>
                                                    {page?.page}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('notification.sendOnDate')}</label>
                                        <input
                                            type="date"
                                            min={new Date().toISOString().split("T")[0]}
                                            value={formData.starting_date}
                                            onChange={(e) => setFormData({ ...formData, starting_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className='notification-label required'>{t('notification.sendOnTime')}</label>
                                        <input
                                            type="time"
                                            value={formData.starting_time}
                                            onChange={(e) => setFormData({ ...formData, starting_time: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="button-group">
                                    <button className="close-btn" onClick={handleOnClose}>{t('common.close')}</button>
                                    <button className="send-btn" onClick={handleSubmit} disabled={!isFormValid() || isLoading}>
                                        {isLoading ? <span className="loader-btn"></span> : (isEditMode ? t('common.update') : t('common.send'))}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    )
}

export default CreateEditNotification